import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/auth'

export function KYCIntro() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const requiredDocuments = [
    {
      icon: '📄',
      title: 'Documento de Identidad',
      description: 'DNI, Pasaporte o NIE vigente',
      details: 'Necesitarás fotos claras del frente y reverso'
    },
    {
      icon: '🏠',
      title: 'Comprobante de Domicilio',
      description: 'Recibo de servicios o estado de cuenta',
      details: 'Debe tener fecha de los últimos 3 meses'
    },
    {
      icon: '🏢',
      title: 'Información del Negocio',
      description: 'Datos de tu empresa o actividad comercial',
      details: 'Nombre, tipo de negocio y categoría de productos'
    },
    {
      icon: '🏦',
      title: 'Datos Bancarios',
      description: 'Información de tu cuenta para recibir pagos',
      details: 'CLABE/IBAN y nombre del titular'
    }
  ]

  const processSteps = [
    'Verificación de tu identidad personal',
    'Validación de tu domicilio actual',
    'Registro de tu información empresarial',
    'Configuración de tu cuenta bancaria',
    'Aceptación de términos y condiciones',
    'Revisión por nuestro equipo (2-3 días hábiles)'
  ]

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-5xl">
        {/* Logout Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
          >
            <span>🚪</span>
            Cerrar Sesión
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-3">
            <span className="text-3xl">🛡️</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Verificación de Vendedor
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Para garantizar la seguridad de todos nuestros usuarios, necesitamos verificar tu identidad
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          {/* What is KYC Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <h2 className="text-xl font-bold mb-2">¿Qué es la Verificación KYC?</h2>
            <p className="text-blue-50 text-sm leading-relaxed">
              KYC (Know Your Customer) es un proceso estándar de verificación de identidad que nos permite
              cumplir con regulaciones de seguridad y prevenir fraudes. Este proceso es obligatorio para
              todos los vendedores y solo necesitas completarlo una vez.
            </p>
          </div>

          {/* Required Documents */}
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Documentos que Necesitarás
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {requiredDocuments.map((doc, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start">
                    <span className="text-3xl mr-3 flex-shrink-0">{doc.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm">{doc.title}</h4>
                      <p className="text-sm text-gray-700 mb-1">{doc.description}</p>
                      <p className="text-xs text-gray-500 italic">{doc.details}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Process Steps */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                Pasos del Proceso
              </h3>
              <div className="space-y-2">
                {processSteps.map((step, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3">
                      {index + 1}
                    </div>
                    <p className="text-gray-700 text-sm pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Time & Privacy */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">⏱️</span>
                  <h4 className="font-semibold text-green-900 text-sm">Tiempo Estimado</h4>
                </div>
                <p className="text-sm text-green-800">
                  Completar el formulario toma aproximadamente <strong>10-15 minutos</strong>.
                  Puedes guardar tu progreso y continuar después.
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">🔒</span>
                  <h4 className="font-semibold text-purple-900 text-sm">Tu Privacidad</h4>
                </div>
                <p className="text-sm text-purple-800">
                  Toda tu información está <strong>encriptada y protegida</strong>. Solo será
                  utilizada para verificación y cumplimiento legal.
                </p>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-amber-800 mb-1">
                    Importante
                  </h3>
                  <div className="text-sm text-amber-700 space-y-0.5">
                    <p>• Asegúrate de que todas las fotos sean claras y legibles</p>
                    <p>• Los documentos deben estar vigentes y no vencidos</p>
                    <p>• La información debe coincidir en todos los documentos</p>
                    <p>• No podrás publicar productos hasta completar la verificación</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
          <button
            onClick={() => navigate('/backoffice')}
            className="px-6 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors text-sm"
          >
            Volver al Inicio
          </button>
          <button
            onClick={() => navigate('/kyc/wizard')}
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all flex items-center gap-2 text-sm"
          >
            Comenzar Verificación
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>

        {/* Support */}
        <p className="text-center mt-6 text-sm text-gray-600">
          ¿Tienes dudas o problemas? Contáctanos en{' '}
          <a href="mailto:support@eshop.com" className="text-blue-600 hover:underline font-medium">
            support@eshop.com
          </a>
        </p>
      </div>
    </div>
  )
}
